/**
 * Phase 5 — Signed launch-replay helpers.
 *
 * Two-step flow so callers can either show the replay in a modal OR
 * trigger a file download, without duplicating fetch logic:
 *   1. `fetchReplay(handle)`   → { bundle, pub } from the API
 *   2. `saveReplayFile(bundle, handle)` → triggers browser download
 *
 * `downloadReplay(handle)` is kept as a convenience wrapper that does
 * both — used by legacy callers where the modal isn't in play.
 *
 * `verifyReplaySignature(bundle, pubKeyB64)` performs an in-browser
 * ed25519 verification (Web Crypto API) so the modal can display a
 * live green ✓ instead of asking users to trust the server that
 * generated the payload in the first place.
 */
import api from '../api';

/** Fetch the signed replay + public key for a clan. */
export async function fetchReplay(handle) {
  if (!handle) throw new Error('handle required');
  const [replayRes, keyRes] = await Promise.all([
    api.get(`/replay/clan/${handle}`),
    api.get('/replay/public-key'),
  ]);
  return { bundle: replayRes.data, pub: keyRes.data };
}

/** Save an already-fetched replay bundle to disk. */
export function saveReplayFile(bundle, pub, handle) {
  const enrichedBundle = {
    ...bundle,
    _verification: {
      note: 'Ed25519-verify `signature` against the canonical JSON of `payload` using `public_key_b64`.',
      public_key_b64: pub.public_key_b64,
      key_id: pub.key_id,
      algorithm: pub.algorithm,
      verify_instructions: pub.verify_instructions,
    },
  };
  const blob = new Blob([JSON.stringify(enrichedBundle, null, 2)], { type: 'application/json' });
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 15);
  const filename = `clanster-replay-${handle}-${ts}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { filename };
}

/** Legacy one-shot: fetch + save. Kept for backward compat. */
export async function downloadReplay(handle) {
  const { bundle, pub } = await fetchReplay(handle);
  const { filename } = saveReplayFile(bundle, pub, handle);
  return { filename, trade_count: bundle.payload?.trade_count ?? 0 };
}

/**
 * Decode base64url (accepts either standard or url-safe alphabet).
 * The server emits url-safe (RFC 4648 §5) so `-` / `_` must be
 * translated back to `+` / `/` before `atob` runs.
 */
function b64urlToBytes(s) {
  const std = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = std + '='.repeat((4 - (std.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Canonical JSON — matches Python `json.dumps(x, sort_keys=True,
 * separators=(',',':'))`. Sorts every object's keys deterministically
 * so the bytes fed to `verify` are identical to the bytes the server
 * signed.
 */
function canonicalStringify(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(canonicalStringify).join(',') + ']';
  const keys = Object.keys(v).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalStringify(v[k])).join(',') + '}';
}

/**
 * Standard base64 (with `+` / `/` and optional `=` padding) → bytes.
 * Used for the server-provided `payload_canonical_b64` which uses the
 * standard alphabet (Python `base64.b64encode`), unlike the URL-safe
 * signature/public-key blobs above.
 */
function b64stdToBytes(s) {
  const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * In-browser ed25519 verification. Returns:
 *   { ok: boolean, reason: string }
 * Uses `crypto.subtle` where available (Chrome 137+, Firefox 130+,
 * Safari 17.4+). On older browsers falls back to returning
 * `ok: null` so the UI can show a friendlier "verify offline" hint.
 *
 * When the server provides `payload_canonical_b64` (the exact bytes
 * that were signed), we verify against those directly — this avoids
 * cross-language JSON canonicalization drift (e.g. Python's `0.0`
 * vs JS's `0`, or non-ASCII escaping differences).
 */
export async function verifyReplaySignature(bundle, pubKeyB64) {
  try {
    if (!bundle?.payload || !bundle?.signature) {
      return { ok: false, reason: 'Missing payload or signature.' };
    }
    if (!crypto?.subtle?.importKey) {
      return { ok: null, reason: 'This browser lacks Ed25519 crypto — verify offline.' };
    }
    const rawKey = b64urlToBytes(pubKeyB64);
    const sig = b64urlToBytes(bundle.signature);
    // Prefer the server-signed canonical bytes when available — this is
    // bit-exact with what the private key signed, so no re-canonicalization
    // drift can invalidate an otherwise-authentic bundle.
    const msg = bundle.payload_canonical_b64
      ? b64stdToBytes(bundle.payload_canonical_b64)
      : new TextEncoder().encode(canonicalStringify(bundle.payload));
    let key;
    try {
      key = await crypto.subtle.importKey('raw', rawKey, { name: 'Ed25519' }, false, ['verify']);
    } catch (e) {
      return { ok: null, reason: 'This browser lacks Ed25519 crypto — verify offline.' };
    }
    const ok = await crypto.subtle.verify({ name: 'Ed25519' }, key, sig, msg);
    return { ok, reason: ok ? 'Signature verified.' : 'Signature does NOT match the payload.' };
  } catch (e) {
    return { ok: false, reason: e?.message || 'Verification failed.' };
  }
}
